import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  Text
} from '@chakra-ui/react'
import DOMPurify from 'dompurify'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import GradientBuilder from '../../../../components/GradientBuilder'
import { RichTextField } from '../../../../components/RichTextField'
import { SectionCard } from '../../../../components/SectionCard'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import { useHasRole, useAppToast } from '../../../../helpers'
import { IPromoBannerModalProps, IPromoBannerPayload } from '../../../../interfaces/PromoBanners/IPromoBanner'
import { CoursesService } from '../../../../services/CoursesService/CoursesService'
import { ISelectCourse } from '../../../../services/CoursesService/interfaces'
import { PromoBannersService } from '../../../../services/PromoBanners/PromoBannersService'

const DEFAULT_GRADIENT = 'linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(103,2,142,1) 50%, rgba(250,0,105,1) 100%)'
const DEFAULT_BUTTON_COLOR = '#000000'

const MONO = "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, monospace"

/**
 * Cursos gravados que têm landing no site com o banner promocional. Os demais
 * gravados ficam de fora: não têm página para exibir o banner e vários repetem
 * o nome do presencial, o que duplicaria as opções e entraria no "Todos".
 */
const RECORDED_COURSES_WITH_SITE_PAGE = ['sketch-automotivo-manual']

interface ICourseOption {
  value: number
  label: string
  promo_banner_id: number | null
  knowledge_areas: { id: number; name: string }[]
}

interface IKnowledgeAreaOption {
  value: number
  label: string
}

const emptyForm = {
  promo_text: '',
  mobile_text: '',
  discount_value: '',
  end_date: '',
  hours_refresh: '',
  days_refresh: '',
  cta_text: '',
  background_color: DEFAULT_GRADIENT,
  button_color: DEFAULT_BUTTON_COLOR,
  cta_text_color: '#FFFFFF',
  discount_text_color: '#FFFFFF',
  timer_numbers_color: '#FFFFFF',
  timer_labels_color: '#FFFFFF',
  course_ids: [] as number[]
}

export default function PromoBannerModal({ isOpen, onClose, editing, onSave }: IPromoBannerModalProps) {
  const service = new PromoBannersService()
  const coursesService = new CoursesService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canCreate = hasRole(['add_promo_banner'])
  const canUpdate = hasRole(['edit_promo_banner'])
  const canEdit = editing ? canUpdate : canCreate

  const [form, setForm] = useState({ ...emptyForm })
  const [loading, setLoading] = useState(false)
  const [allCourses, setAllCourses] = useState<ICourseOption[]>([])
  const [knowledgeAreas, setKnowledgeAreas] = useState<IKnowledgeAreaOption[]>([])
  const [selectedKnowledgeArea, setSelectedKnowledgeArea] = useState<number | null>(null)
  const [isAreaConfirmOpen, setIsAreaConfirmOpen] = useState(false)
  const [pendingAreaId, setPendingAreaId] = useState<number | null>(null)
  const [isAddAllMode, setIsAddAllMode] = useState(false)
  const cancelAreaRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen) {
      setIsAreaConfirmOpen(false)
      setPendingAreaId(null)
      setIsAddAllMode(false)
    }
  }, [isOpen])

  const handleBackgroundChange = useCallback((val: string) => {
    setForm((p) => ({ ...p, background_color: val }))
  }, [])

  const handleButtonColorChange = useCallback((val: string) => {
    setForm((p) => ({ ...p, button_color: val }))
  }, [])

  const handleCtaTextColorChange = useCallback((val: string) => {
    setForm((p) => ({ ...p, cta_text_color: val }))
  }, [])

  const handleDiscountTextColorChange = useCallback((val: string) => {
    setForm((p) => ({ ...p, discount_text_color: val }))
  }, [])

  const handleTimerNumbersColorChange = useCallback((val: string) => {
    setForm((p) => ({ ...p, timer_numbers_color: val }))
  }, [])

  const handleTimerLabelsColorChange = useCallback((val: string) => {
    setForm((p) => ({ ...p, timer_labels_color: val }))
  }, [])

  useEffect(() => {
    if (!isOpen) {
      // Limpar estados do diálogo quando o modal fechar
      setIsAreaConfirmOpen(false)
      setPendingAreaId(null)
      return
    }
    loadCourses()
    loadKnowledgeAreas()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (editing) {
      loadBanner(editing.id)
    } else {
      setForm({ ...emptyForm })
      setSelectedKnowledgeArea(null)
    }
  }, [editing, isOpen])

  const loadBanner = async (id: number) => {
    try {
      const banner = await service.show(id)
      setForm({
        promo_text: banner.promo_text,
        mobile_text: banner.mobile_text ?? '',
        discount_value: banner.discount_value != null ? String(banner.discount_value) : '',
        end_date: banner.end_date ? banner.end_date.slice(0, 16) : '',
        hours_refresh: banner.hours_refresh != null ? String(banner.hours_refresh) : '',
        days_refresh: banner.days_refresh != null ? String(banner.days_refresh) : '',
        cta_text: banner.cta_text,
        background_color: banner.background_color || DEFAULT_GRADIENT,
        button_color: banner.button_color || DEFAULT_BUTTON_COLOR,
        cta_text_color: banner.cta_text_color || '#FFFFFF',
        discount_text_color: banner.discount_text_color || '#FFFFFF',
        timer_numbers_color: banner.timer_numbers_color || '#FFFFFF',
        timer_labels_color: banner.timer_labels_color || '#FFFFFF',
        course_ids: banner.course_ids ?? []
      })
    } catch {
      toast.error('Erro ao carregar dados do banner')
    }
  }

  const loadCourses = async () => {
    try {
      const data = await coursesService.listForSelect(true, true, true)
      const isRecordedException = (c: ISelectCourse) =>
        !c.in_person && !c.live_online && RECORDED_COURSES_WITH_SITE_PAGE.includes(c.slug ?? '')
      setAllCourses(
        data
          .filter((c) => c.in_person || c.live_online || isRecordedException(c))
          .map((c) => ({
            value: c.id,
            // O presencial pode ter o mesmo nome: o sufixo separa as duas opções
            label: isRecordedException(c) ? `${c.name} (online gravado)` : c.name,
            promo_banner_id: c.promo_banner_id ?? null,
            knowledge_areas: c.knowledge_areas ?? []
          }))
      )
    } catch {
      // silently fail
    }
  }

  const loadKnowledgeAreas = async () => {
    try {
      const data = await coursesService.getKnowledgeAreas()
      const areas = data.map((a: { id: number; name: string }) => ({ value: a.id, label: a.name }))
      // Prepend "Todos" option
      setKnowledgeAreas([{ value: -1, label: 'Todos' }, ...areas])
    } catch {
      // silently fail
    }
  }

  const handleKnowledgeAreaSelect = (areaId: number) => {
    // Handle "Todos" special case (areaId === -1)
    if (areaId === -1) {
      if (form.course_ids.length === 0) {
        // No courses selected yet, add all courses directly
        const allCourseIds = allCourses.map((c) => c.value)
        setSelectedKnowledgeArea(areaId)
        setForm((prev) => ({ ...prev, course_ids: allCourseIds }))
      } else {
        // Courses already selected, show simplified alert
        setPendingAreaId(areaId)
        setIsAddAllMode(true)
        setIsAreaConfirmOpen(true)
      }
      return
    }

    // Handle regular knowledge area selection
    if (form.course_ids.length === 0) {
      const newAreaCourseIds = allCourses
        .filter((c) => c.knowledge_areas.some((ka) => ka.id === areaId))
        .map((c) => c.value)
      setSelectedKnowledgeArea(areaId)
      setForm((prev) => ({ ...prev, course_ids: newAreaCourseIds }))
    } else {
      setPendingAreaId(areaId)
      setIsAddAllMode(false)
      setIsAreaConfirmOpen(true)
    }
  }

  const handleAreaAdd = () => {
    if (pendingAreaId === null) return
    const newAreaCourseIds = allCourses
      .filter((c) => c.knowledge_areas.some((ka) => ka.id === pendingAreaId))
      .map((c) => c.value)
    setSelectedKnowledgeArea(pendingAreaId)
    setForm((prev) => ({ ...prev, course_ids: Array.from(new Set([...prev.course_ids, ...newAreaCourseIds])) }))
    setPendingAreaId(null)
    setIsAreaConfirmOpen(false)
  }

  const handleAreaReplace = () => {
    if (pendingAreaId === null) return
    const newAreaCourseIds = allCourses
      .filter((c) => c.knowledge_areas.some((ka) => ka.id === pendingAreaId))
      .map((c) => c.value)
    setSelectedKnowledgeArea(pendingAreaId)
    setForm((prev) => ({ ...prev, course_ids: newAreaCourseIds }))
    setPendingAreaId(null)
    setIsAreaConfirmOpen(false)
  }

  const handleAddAll = () => {
    if (pendingAreaId === null) return
    const allCourseIds = allCourses.map((c) => c.value)
    setSelectedKnowledgeArea(pendingAreaId)
    setForm((prev) => ({ ...prev, course_ids: Array.from(new Set([...prev.course_ids, ...allCourseIds])) }))
    setPendingAreaId(null)
    setIsAddAllMode(false)
    setIsAreaConfirmOpen(false)
  }

  const handleAreaCancel = () => {
    setPendingAreaId(null)
    setIsAddAllMode(false)
    setIsAreaConfirmOpen(false)
  }

  const handleSubmit = async () => {
    if (!form.promo_text.trim()) {
      toast.warning('O texto promocional é obrigatório')
      return
    }
    if (!form.cta_text.trim()) {
      toast.warning('O texto do botão CTA é obrigatório')
      return
    }

    setLoading(true)
    try {
      const payload: IPromoBannerPayload = {
        promo_text: form.promo_text,
        mobile_text: form.mobile_text || null,
        discount_value: form.discount_value !== '' ? Number(form.discount_value) : null,
        cta_text: form.cta_text,
        background_color: form.background_color,
        button_color: form.button_color,
        cta_text_color: form.cta_text_color,
        discount_text_color: form.discount_text_color,
        timer_numbers_color: form.timer_numbers_color,
        timer_labels_color: form.timer_labels_color,
        course_ids: form.course_ids,
        ...(form.end_date && { end_date: form.end_date }),
        ...(form.hours_refresh && { hours_refresh: Number(form.hours_refresh) }),
        ...(form.days_refresh && { days_refresh: Number(form.days_refresh) })
      }

      if (editing) {
        await service.update(editing.id, payload)
        toast.success('Banner atualizado com sucesso')
      } else {
        await service.create(payload)
        toast.success('Banner criado com sucesso')
      }
      onSave()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao salvar o banner. Tente novamente.'
      toast.error(msg, err)
    } finally {
      setLoading(false)
    }
  }

  const timerField = form.end_date
    ? 'end_date'
    : form.hours_refresh
      ? 'hours_refresh'
      : form.days_refresh
        ? 'days_refresh'
        : null

  const discountNum = form.discount_value !== '' ? Number(form.discount_value) : null

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Montserrat:wght@300;600;800&display=swap"
        rel="stylesheet"
      />
      <style>{`
        @keyframes abraPulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(0.75); }
        }
        @keyframes abraSweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      <FormModal isOpen={isOpen} onClose={onClose} size="editor">
        {/* ===== HEADER ===== */}
        <Flex
          px={8}
          pt={6}
          pb={5}
          align="flex-start"
          justify="space-between"
          gap={6}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          position="relative"
          flexShrink={0}
        >
          <Box flex={1} minW={0}>
            <Flex align="center" gap={2} mb="6px">
              <Box w="5px" h="5px" borderRadius="full" bg="magenta.500" />
              <Text
                fontFamily={MONO}
                fontSize="10px"
                letterSpacing="0.28em"
                textTransform="uppercase"
                color="magenta.500"
                fontWeight={600}
              >
                {editing ? 'Edição · Banner Promocional' : 'Novo · Banner Promocional'}
              </Text>
            </Flex>
            <Text fontSize="22px" fontWeight={700} color="gray.900" letterSpacing="-0.015em" lineHeight={1.15}>
              {editing ? 'Editar banner promocional' : 'Criar banner promocional'}
            </Text>
            <Text variant="caption" mt="4px">
              Texto, cronômetro, público e estilo — a oferta ganha forma conforme você edita.
            </Text>
          </Box>
          <ModalCloseButton position="static" top="unset" right="unset" borderRadius="full" size="md" />
        </Flex>

        {/* ===== FIXED PREVIEW ===== */}
        <Box
          position="relative"
          flexShrink={0}
          bg="#0D0E12"
          borderBottom="1px solid"
          borderColor="#1A1B22"
          px={8}
          py={5}
          overflow="hidden"
        >
          {/* Dot grid canvas */}
          <Box
            position="absolute"
            inset={0}
            bgImage="radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)"
            bgSize="14px 14px"
            pointerEvents="none"
          />
          {/* Corner glow — brand accent */}
          <Box
            position="absolute"
            top="-60%"
            right="-5%"
            w="45%"
            h="220%"
            bg="radial-gradient(circle, rgba(250, 0, 105, 0.14) 0%, transparent 65%)"
            pointerEvents="none"
            filter="blur(30px)"
          />
          <Box
            position="absolute"
            bottom="-60%"
            left="-5%"
            w="35%"
            h="180%"
            bg="radial-gradient(circle, rgba(103, 2, 142, 0.14) 0%, transparent 65%)"
            pointerEvents="none"
            filter="blur(30px)"
          />

          {/* Label bar */}
          <Flex position="relative" align="center" justify="space-between" mb={4}>
            <Flex align="center" gap={3}>
              <Text
                fontFamily={MONO}
                fontSize="10px"
                letterSpacing="0.3em"
                textTransform="uppercase"
                color="gray.500"
                fontWeight={500}
              >
                Preview
              </Text>
              <Box w="1px" h="10px" bg="#2A2C35" />
              <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.3em" textTransform="uppercase" color="gray.500">
                Tempo real
              </Text>
            </Flex>
            <Flex
              align="center"
              gap={2}
              px="10px"
              py="4px"
              borderRadius="full"
              bg="rgba(34, 197, 94, 0.08)"
              border="1px solid"
              borderColor="rgba(34, 197, 94, 0.22)"
            >
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg="#4ADE80"
                animation="abraPulseDot 1.6s ease-in-out infinite"
                boxShadow="0 0 10px rgba(74, 222, 128, 0.7)"
              />
              <Text
                fontFamily={MONO}
                fontSize="9px"
                letterSpacing="0.32em"
                color="#86EFAC"
                textTransform="uppercase"
                fontWeight={600}
              >
                Live
              </Text>
            </Flex>
          </Flex>

          {/* Banner framed */}
          <Box
            position="relative"
            borderRadius="10px"
            overflow="hidden"
            boxShadow="0 20px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)"
          >
            <BannerPreview
              promoText={form.promo_text}
              discountValue={discountNum}
              endDate={form.end_date}
              hoursRefresh={form.hours_refresh}
              daysRefresh={form.days_refresh}
              ctaText={form.cta_text || 'BOTÃO CTA'}
              backgroundColor={form.background_color}
              buttonColor={form.button_color}
              ctaTextColor={form.cta_text_color}
              discountTextColor={form.discount_text_color}
              timerNumbersColor={form.timer_numbers_color}
              timerLabelsColor={form.timer_labels_color}
            />
          </Box>
        </Box>

        {/* ===== SCROLLABLE FORM ===== */}
        <Box
          flex={1}
          overflow="auto"
          px={8}
          py={7}
          bg="gray.50"
          sx={{
            '&::-webkit-scrollbar': { width: '10px' },
            '&::-webkit-scrollbar-track': { bg: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              bg: 'rgba(0,0,0,0.12)',
              borderRadius: '10px',
              border: '2px solid',
              borderColor: 'gray.50'
            },
            '&::-webkit-scrollbar-thumb:hover': { bg: 'rgba(0,0,0,0.2)' }
          }}
        >
          <Section number="01" title="Mensagem" hint="A copy que o aluno vê primeiro.">
            <SectionCard>
              <RichTextField
                label="Texto promocional"
                isRequired
                toolbar="color"
                minH="100px"
                value={form.promo_text}
                onChange={(promo_text) => setForm((p) => ({ ...p, promo_text }))}
              />

              <RichTextField
                label="Texto mobile (opcional)"
                mt={3}
                toolbar="color"
                minH="100px"
                value={form.mobile_text}
                onChange={(mobile_text) => setForm((p) => ({ ...p, mobile_text }))}
              />

              <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={4} mt={3}>
                <FormField label="Texto do botão CTA" isRequired>
                  <Input
                    size="md"
                    bg="white"
                    borderColor="gray.200"
                    _focusVisible={{
                      borderColor: 'magenta.400',
                      boxShadow: '0 0 0 1px var(--chakra-colors-magenta-400)'
                    }}
                    value={form.cta_text}
                    onChange={(e) => setForm((p) => ({ ...p, cta_text: e.target.value }))}
                    placeholder="Ex: SE INSCREVA AGORA"
                  />
                </FormField>
                <FormField label="Desconto (%)">
                  <NumberInput
                    min={0}
                    max={100}
                    value={form.discount_value}
                    onChange={(val) => setForm((p) => ({ ...p, discount_value: val }))}
                    size="md"
                  >
                    <NumberInputField
                      bg="white"
                      borderColor="gray.200"
                      fontFamily={MONO}
                      _focusVisible={{
                        borderColor: 'magenta.400',
                        boxShadow: '0 0 0 1px var(--chakra-colors-magenta-400)'
                      }}
                    />
                  </NumberInput>
                </FormField>
              </Grid>
            </SectionCard>
          </Section>

          <Section number="02" title="Cronômetro" hint="Escolha apenas uma forma de temporização.">
            <SectionCard>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                <TimerCell
                  label="Data de encerramento"
                  active={timerField === 'end_date'}
                  dimmed={!!timerField && timerField !== 'end_date'}
                >
                  <Input
                    type="datetime-local"
                    size="md"
                    bg="white"
                    borderColor="gray.200"
                    value={form.end_date}
                    isDisabled={!!timerField && timerField !== 'end_date'}
                    onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                    fontFamily={MONO}
                    fontSize="13px"
                  />
                </TimerCell>
                <TimerCell
                  label="Renovar a cada X horas"
                  active={timerField === 'hours_refresh'}
                  dimmed={!!timerField && timerField !== 'hours_refresh'}
                >
                  <NumberInput
                    min={1}
                    size="md"
                    value={form.hours_refresh}
                    isDisabled={!!timerField && timerField !== 'hours_refresh'}
                    onChange={(val) => setForm((p) => ({ ...p, hours_refresh: val }))}
                  >
                    <NumberInputField bg="white" borderColor="gray.200" fontFamily={MONO} />
                  </NumberInput>
                </TimerCell>
                <TimerCell
                  label="Renovar a cada X dias"
                  active={timerField === 'days_refresh'}
                  dimmed={!!timerField && timerField !== 'days_refresh'}
                >
                  <NumberInput
                    min={1}
                    size="md"
                    value={form.days_refresh}
                    isDisabled={!!timerField && timerField !== 'days_refresh'}
                    onChange={(val) => setForm((p) => ({ ...p, days_refresh: val }))}
                  >
                    <NumberInputField bg="white" borderColor="gray.200" fontFamily={MONO} />
                  </NumberInput>
                </TimerCell>
              </Grid>
              {timerField && (
                <Flex mt={4} align="center" justify="space-between">
                  <Flex align="center" gap={2}>
                    <Box w="4px" h="4px" borderRadius="full" bg="magenta.500" />
                    <Text
                      fontFamily={MONO}
                      fontSize="10px"
                      letterSpacing="0.2em"
                      textTransform="uppercase"
                      color="gray.500"
                    >
                      Ativo:{' '}
                      {timerField === 'end_date'
                        ? 'Data fixa'
                        : timerField === 'hours_refresh'
                          ? 'Renovação por horas'
                          : 'Renovação por dias'}
                    </Text>
                  </Flex>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    fontFamily={MONO}
                    fontSize="10px"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    onClick={() => setForm((p) => ({ ...p, end_date: '', hours_refresh: '', days_refresh: '' }))}
                  >
                    Limpar
                  </Button>
                </Flex>
              )}
            </SectionCard>
          </Section>

          <Section number="03" title="Público" hint="Quem será impactado pela oferta.">
            <SectionCard>
              <Grid templateColumns={{ base: '1fr', md: '1fr 2fr' }} gap={4}>
                <GridItem>
                  <FormField label="Por área de conhecimento">
                    <FormSelect
                      bg="white"
                      value={selectedKnowledgeArea ?? ''}
                      onChange={(value) => {
                        if (value) handleKnowledgeAreaSelect(Number(value))
                      }}
                      options={knowledgeAreas.map((area) => ({ value: area.value, label: area.label }))}
                      placeholder="Selecione uma área..."
                    />
                  </FormField>
                </GridItem>
                <GridItem>
                  {/* O contador anda junto com o rótulo, então ele é o rótulo: o `label` do
                      `FormField` aceita nó, e o `FormLabel` vira a própria linha. */}
                  <FormField
                    label={
                      <Flex justify="space-between" align="center">
                        Cursos vinculados
                        <Text
                          fontFamily={MONO}
                          fontSize="10px"
                          letterSpacing="0.2em"
                          color="gray.400"
                          textTransform="uppercase"
                        >
                          {form.course_ids.length} {form.course_ids.length === 1 ? 'curso' : 'cursos'}
                        </Text>
                      </Flex>
                    }
                  >
                    <SearchableSelect<number>
                      isMulti
                      options={allCourses}
                      value={form.course_ids}
                      onChange={(courseIds) => setForm((p) => ({ ...p, course_ids: courseIds }))}
                      placeholder="Selecione os cursos..."
                      emptyMessage="Nenhum curso disponível"
                    />
                  </FormField>
                </GridItem>
              </Grid>
            </SectionCard>
          </Section>

          <Section number="04" title="Estilo" hint="Identidade visual do banner.">
            <SectionCard>
              <FormField label="Cor do botão CTA" isRequired mb={5}>
                <ButtonColorField value={form.button_color} onChange={handleButtonColorChange} />
              </FormField>

              <FormField label="Cor do texto do botão CTA" mb={5}>
                <ButtonColorField value={form.cta_text_color} onChange={handleCtaTextColorChange} />
              </FormField>

              <FormField label="Cor do texto do desconto" mb={5}>
                <ButtonColorField value={form.discount_text_color} onChange={handleDiscountTextColorChange} />
              </FormField>

              <FormField label="Cor dos números do cronômetro" mb={5}>
                <ButtonColorField value={form.timer_numbers_color} onChange={handleTimerNumbersColorChange} />
              </FormField>

              <FormField label="Cor dos labels do cronômetro" mb={5}>
                <ButtonColorField value={form.timer_labels_color} onChange={handleTimerLabelsColorChange} />
              </FormField>

              <FormField label="Fundo do banner (gradiente)" isRequired>
                <GradientBuilder value={form.background_color} onChange={handleBackgroundChange} />
              </FormField>
            </SectionCard>
          </Section>
        </Box>

        {/* ===== FOOTER ===== */}
        <Flex
          px={8}
          py={4}
          borderTop="1px solid"
          borderColor="gray.100"
          bg="white"
          justify="space-between"
          align="center"
          flexShrink={0}
          gap={4}
        >
          <Flex align="center" gap={2}>
            <Box w="6px" h="6px" borderRadius="full" bg={editing ? 'magenta.500' : 'gray.300'} />
            <Text
              fontFamily={MONO}
              fontSize="10px"
              letterSpacing="0.22em"
              textTransform="uppercase"
              color="gray.500"
              fontWeight={500}
            >
              {editing ? `Banner #${editing.id}` : 'Rascunho não salvo'}
            </Text>
          </Flex>
          <Flex gap={2}>
            <Button variant="ghost" onClick={onClose} isDisabled={loading} color="gray.600">
              Cancelar
            </Button>
            <Button
              colorScheme="magenta"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={!canEdit}
              minW="180px"
              fontWeight={600}
              letterSpacing="0.01em"
              boxShadow="0 10px 20px -6px rgba(250, 0, 105, 0.4)"
              _hover={{ boxShadow: '0 14px 28px -8px rgba(250, 0, 105, 0.5)', transform: 'translateY(-1px)' }}
              transition="all 0.2s"
            >
              {editing ? 'Salvar alterações' : 'Criar banner'}
            </Button>
          </Flex>
        </Flex>
      </FormModal>

      <AlertDialog
        isOpen={isOpen && isAreaConfirmOpen}
        leastDestructiveRef={cancelAreaRef}
        onClose={handleAreaCancel}
        isCentered
        size="md"
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px">
            <AlertDialogHeader fontSize="16px" fontWeight={700} color="gray.900" pb={2}>
              {isAddAllMode ? 'Adicionar todos os cursos' : 'Cursos já vinculados'}
            </AlertDialogHeader>
            <AlertDialogBody fontSize="14px" color="gray.600">
              {isAddAllMode
                ? 'Você já tem cursos vinculados. Deseja adicionar todos os cursos aos existentes?'
                : 'Você já tem cursos vinculados. O que deseja fazer com os cursos desta área de conhecimento?'}
            </AlertDialogBody>
            <AlertDialogFooter gap={2} flexWrap="wrap">
              <Button ref={cancelAreaRef} variant="ghost" onClick={handleAreaCancel} color="gray.600" size="sm">
                Cancelar
              </Button>
              {isAddAllMode ? (
                <Button colorScheme="green" onClick={handleAddAll} size="sm">
                  Adicionar todos
                </Button>
              ) : (
                <>
                  <Button colorScheme="blue" variant="outline" onClick={handleAreaAdd} size="sm">
                    Adicionar aos existentes
                  </Button>
                  <Button colorScheme="red" onClick={handleAreaReplace} size="sm">
                    Substituir
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

// ============================================================
// Helper layout primitives
// ============================================================

function Section({
  number,
  title,
  hint,
  children
}: {
  number: string
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <Box mb={7}>
      <Flex align="center" gap={3} mb={3}>
        <Flex
          align="center"
          justify="center"
          px="8px"
          h="22px"
          minW="32px"
          borderRadius="5px"
          bg="gray.900"
          color="white"
          fontFamily={MONO}
          fontSize="10px"
          fontWeight={600}
          letterSpacing="0.08em"
        >
          {number}
        </Flex>
        <Text
          fontFamily={MONO}
          fontSize="11px"
          fontWeight={600}
          color="gray.800"
          letterSpacing="0.24em"
          textTransform="uppercase"
        >
          {title}
        </Text>
        <Box flex={1} h="1px" bgGradient="linear(to-r, gray.200, transparent)" />
        {hint && (
          <Text fontSize="12px" color="gray.500" letterSpacing="0.01em">
            {hint}
          </Text>
        )}
      </Flex>
      <Box>{children}</Box>
    </Box>
  )
}

// Isolates native color input drag churn from the rest of the modal.
// Local state drives the swatch instantly; a rAF-throttled ref propagates
// the latest color to the parent at most once per frame so heavy siblings
// (GradientBuilder/ColorPicker) don't re-render per pointer move.
function ButtonColorField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [local, setLocal] = useState(value)
  const rafRef = useRef<number | null>(null)
  const latestRef = useRef(value)

  useEffect(() => {
    if (value !== local) setLocal(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    },
    []
  )

  const handleChange = (next: string) => {
    setLocal(next)
    latestRef.current = next
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      onChange(latestRef.current)
    })
  }

  return (
    <Flex align="center" gap={3} p={3} bg="gray.50" borderRadius="10px" border="1px solid" borderColor="gray.200">
      <Box
        position="relative"
        w="44px"
        h="44px"
        borderRadius="8px"
        overflow="hidden"
        cursor="pointer"
        flexShrink={0}
        boxShadow="inset 0 0 0 2px white, 0 0 0 1px rgba(0,0,0,0.08)"
      >
        <Box position="absolute" inset={0} bg={local} />
        <Input
          type="color"
          position="absolute"
          inset={0}
          width="100%"
          height="100%"
          p={0}
          border="none"
          opacity={0}
          cursor="pointer"
          value={local}
          onChange={(e) => handleChange(e.target.value)}
        />
      </Box>
      <Box>
        <Text fontFamily={MONO} fontSize="14px" color="gray.800" fontWeight={600} letterSpacing="0.02em">
          {local.toUpperCase()}
        </Text>
        <Text
          fontFamily={MONO}
          fontSize="10px"
          color="gray.500"
          letterSpacing="0.15em"
          textTransform="uppercase"
          mt="2px"
        >
          Clique no quadrado para editar
        </Text>
      </Box>
    </Flex>
  )
}

function TimerCell({
  label,
  active,
  dimmed,
  children
}: {
  label: string
  active: boolean
  dimmed: boolean
  children: ReactNode
}) {
  return (
    <Box
      p={3}
      borderRadius="10px"
      border="1px solid"
      borderColor={active ? 'magenta.300' : 'gray.200'}
      bg={active ? 'magenta.50' : 'gray.50'}
      opacity={dimmed ? 0.4 : 1}
      transition="all 0.2s"
      position="relative"
    >
      <Flex align="center" gap={2} mb={2}>
        <Box w="5px" h="5px" borderRadius="full" bg={active ? 'magenta.500' : 'gray.300'} transition="all 0.2s" />
        <Text
          fontFamily={MONO}
          fontSize="10px"
          fontWeight={500}
          letterSpacing="0.14em"
          textTransform="uppercase"
          color={active ? 'magenta.600' : 'gray.500'}
        >
          {label}
        </Text>
      </Flex>
      {children}
    </Box>
  )
}

// ============================================================
// Banner Preview
// ============================================================

interface IBannerPreviewProps {
  promoText: string
  discountValue: number | null
  endDate: string
  hoursRefresh: string
  daysRefresh: string
  ctaText: string
  backgroundColor: string
  buttonColor: string
  ctaTextColor: string
  discountTextColor: string
  timerNumbersColor: string
  timerLabelsColor: string
}

function BannerPreview({
  promoText,
  discountValue,
  endDate,
  hoursRefresh,
  daysRefresh,
  ctaText,
  backgroundColor,
  buttonColor,
  ctaTextColor,
  discountTextColor,
  timerNumbersColor,
  timerLabelsColor
}: IBannerPreviewProps) {
  const fontFamily = "'Montserrat', sans-serif"
  const white = '#ffffff'

  const [timeLeft, setTimeLeft] = useState<number>(0)

  useEffect(() => {
    let targetMs: number | null = null
    if (endDate) {
      targetMs = new Date(endDate).getTime() - Date.now()
    } else if (hoursRefresh) {
      targetMs = Number(hoursRefresh) * 3600 * 1000
    } else if (daysRefresh) {
      targetMs = Number(daysRefresh) * 86400 * 1000
    }

    if (!targetMs || targetMs <= 0) {
      setTimeLeft(0)
      return
    }

    setTimeLeft(Math.floor(targetMs / 1000))
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(interval)
  }, [endDate, hoursRefresh, daysRefresh])

  const hasTimer = !!endDate || !!hoursRefresh || !!daysRefresh

  const d = Math.floor(timeLeft / 86400)
  const h = Math.floor((timeLeft % 86400) / 3600)
  const m = Math.floor((timeLeft % 3600) / 60)
  const s = timeLeft % 60
  const pad = (n: number) => String(n).padStart(2, '0')

  // Esta é a prévia do banner do site, com a tipografia dele — passar pelo `<WysiwygContent>`
  // traria junto a folha de estilo do painel e a prévia deixaria de parecer com o que o
  // visitante vê. O que ela precisa do componente é a sanitização, e é só ela que vem aqui:
  // a mesma exceção que o `ArtFieldsPreview` já tem no eixo das máscaras.
  const sanitizedPromoText = DOMPurify.sanitize(promoText || '<span>Texto promocional</span>')

  return (
    <Box w="100%" style={{ background: backgroundColor }} px={4} py={3} overflow="hidden">
      <Flex align="center" justify="space-between" gap={3} wrap="nowrap" minH="60px">
        <Box
          color={white}
          fontFamily={fontFamily}
          fontSize="18px"
          fontWeight={300}
          dangerouslySetInnerHTML={{ __html: sanitizedPromoText }}
          sx={{
            'strong, b': { fontWeight: 800 },
            minW: '120px'
          }}
        />

        {discountValue != null && (
          <Text
            color={discountTextColor}
            fontFamily={fontFamily}
            fontSize="36px"
            fontWeight={800}
            whiteSpace="nowrap"
            flexShrink={0}
          >
            {discountValue}% OFF
          </Text>
        )}

        {hasTimer && (
          <Flex align="center" gap={3} flexShrink={0}>
            <Box w="1px" h="50px" bg={white} />
            <Flex align="flex-end" gap={1}>
              <TimerUnit
                value={pad(d)}
                label="Dias"
                fontFamily={fontFamily}
                numbersColor={timerNumbersColor}
                labelsColor={timerLabelsColor}
              />
              <Box w="1px" h="32px" bg={white} mb="14px" />
              <TimerUnit
                value={pad(h)}
                label="Hora"
                fontFamily={fontFamily}
                numbersColor={timerNumbersColor}
                labelsColor={timerLabelsColor}
              />
              <Box w="1px" h="32px" bg={white} mb="14px" />
              <TimerUnit
                value={pad(m)}
                label="Minutos"
                fontFamily={fontFamily}
                numbersColor={timerNumbersColor}
                labelsColor={timerLabelsColor}
              />
              <Box w="1px" h="32px" bg={white} mb="14px" />
              <TimerUnit
                value={pad(s)}
                label="Segundos"
                fontFamily={fontFamily}
                numbersColor={timerNumbersColor}
                labelsColor={timerLabelsColor}
              />
            </Flex>
          </Flex>
        )}

        <Box
          as="button"
          bg={buttonColor}
          color={ctaTextColor}
          fontFamily={fontFamily}
          fontSize="14px"
          fontWeight={600}
          textTransform="uppercase"
          px={5}
          py={2}
          borderRadius="md"
          whiteSpace="nowrap"
          flexShrink={0}
          letterSpacing="0.05em"
        >
          {ctaText}
        </Box>
      </Flex>
    </Box>
  )
}

function TimerUnit({
  value,
  label,
  fontFamily,
  numbersColor,
  labelsColor
}: {
  value: string
  label: string
  fontFamily: string
  numbersColor: string
  labelsColor: string
}) {
  return (
    <Flex direction="column" align="center" minW="36px">
      <Text color={numbersColor} fontFamily={fontFamily} fontSize="28px" fontWeight={300} lineHeight={1}>
        {value}
      </Text>
      <Text color={labelsColor} fontFamily={fontFamily} fontSize="10px" fontWeight={300} mt="2px">
        {label}
      </Text>
    </Flex>
  )
}
